import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { Sidebar } from "../components/layout/Sidebar";
import { useSidebar } from "../contexts/SidebarContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { toast } from "sonner";
import { FileSpreadsheet, Upload, Crown, CheckCircle, XCircle, Download, FileText, AlertTriangle, List, ArrowRight } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// CSV Parser
const parseCSV = (text) => {
  const lines = text.trim().split('\n');
  if (lines.length < 2) return [];
  
  const headers = lines[0].split(',').map(h => h.trim().toLowerCase().replace(/[^a-z0-9_]/g, '_'));
  const data = [];
  
  for (let i = 1; i < lines.length; i++) {
    const values = lines[i].split(',').map(v => v.trim().replace(/^"|"$/g, ''));
    const row = {};
    headers.forEach((header, idx) => {
      row[header] = values[idx] || '';
    });
    data.push(row);
  }
  
  return data;
};

export default function ListImportPage({ user, subscription, onLogout }) {
  const { mainClass } = useSidebar();
  const navigate = useNavigate();
  const [file, setFile] = useState(null);
  const [listName, setListName] = useState("");
  const [parsedData, setParsedData] = useState([]);
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const [columnMapping, setColumnMapping] = useState({});

  const tier = subscription?.tier || "starter";

  const requiredFields = [
    { key: 'property_address', label: 'Property Address', required: true },
    { key: 'city', label: 'City', required: false },
    { key: 'state', label: 'State', required: false },
    { key: 'zip_code', label: 'ZIP Code', required: false },
    { key: 'asking_price', label: 'Asking Price', required: false },
    { key: 'bedrooms', label: 'Bedrooms', required: false },
    { key: 'bathrooms', label: 'Bathrooms', required: false },
    { key: 'sqft', label: 'Square Feet', required: false },
    { key: 'owner_name', label: 'Owner Name', required: false },
    { key: 'owner_phone', label: 'Owner Phone', required: false },
    { key: 'owner_email', label: 'Owner Email', required: false },
  ];

  const handleFileUpload = (e) => {
    const uploadedFile = e.target.files[0];
    if (!uploadedFile) return;

    if (!uploadedFile.name.endsWith('.csv')) {
      toast.error("Please upload a CSV file");
      return;
    }

    setFile(uploadedFile);
    setImportResult(null);
    
    // Set default list name from filename
    const defaultName = uploadedFile.name.replace('.csv', '');
    setListName(defaultName);

    const reader = new FileReader();
    reader.onload = (event) => {
      const text = event.target.result;
      const data = parseCSV(text);
      setParsedData(data);
      
      // Auto-map columns
      if (data.length > 0) {
        const headers = Object.keys(data[0]);
        const autoMapping = {};
        
        requiredFields.forEach(field => {
          const match = headers.find(h => 
            h === field.key || 
            h.includes(field.key.replace(/_/g, '')) ||
            h.includes(field.label.toLowerCase().replace(/\s/g, ''))
          );
          if (match) autoMapping[field.key] = match;
        });
        
        setColumnMapping(autoMapping);
      }
      
      toast.success(`Parsed ${data.length} rows from CSV`);
    };
    reader.readAsText(uploadedFile);
  };

  const handleImport = async () => {
    if (parsedData.length === 0) {
      toast.error("No data to import");
      return;
    }

    if (!listName.trim()) {
      toast.error("Please enter a list name");
      return;
    }

    setImporting(true);
    try {
      // Map data using column mapping
      const mappedData = parsedData.map(row => {
        const mapped = {};
        Object.entries(columnMapping).forEach(([targetField, sourceColumn]) => {
          if (sourceColumn && row[sourceColumn] !== undefined) {
            mapped[targetField] = row[sourceColumn];
          }
        });
        return mapped;
      });

      const response = await axios.post(
        `${BACKEND_URL}/api/imported-lists`,
        { 
          name: listName.trim(),
          source: "CSV Import",
          leads: mappedData 
        },
        { withCredentials: true }
      );

      setImportResult(response.data);
      toast.success(response.data.message);
      
      // Clear form after successful import
      setTimeout(() => {
        navigate('/imported-lists');
      }, 2000);
    } catch (error) {
      toast.error("Import failed: " + (error.response?.data?.detail || error.message));
    } finally {
      setImporting(false);
    }
  };

  const downloadTemplate = () => {
    const headers = requiredFields.map(f => f.key).join(',');
    const sampleRow = '123 Main St,Springfield,IL,62701,150000,3,2,1500,John Doe,555-123-4567,john@example.com';
    const csv = `${headers}\n${sampleRow}`;
    
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'dealforge_import_template.csv';
    a.click();
    URL.revokeObjectURL(url);
  };

  // Feature gate
  if (tier === "starter") {
    return (
      <div className="min-h-screen bg-black flex">
        <Sidebar user={user} subscription={subscription} onLogout={onLogout} />
        <main className={`${mainClass} p-4 md:p-8`}>
          <div className="max-w-2xl mx-auto text-center py-16">
            <div className="w-20 h-20 bg-gold/10 border border-gold/30 flex items-center justify-center mx-auto mb-6">
              <FileSpreadsheet className="w-10 h-10 text-gold" />
            </div>
            <h1 className="font-heading text-3xl font-bold text-white uppercase mb-4">List Import</h1>
            <p className="text-white/70 mb-8">Bulk import leads from CSV files to manage before adding to your pipeline.</p>
            <Link to="/upgrade">
              <Button className="gold-gradient-btn rounded-none font-bold px-8 h-12">
                <Crown className="w-5 h-5 mr-2" />Upgrade to Pro
              </Button>
            </Link>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black flex">
      <Sidebar user={user} subscription={subscription} onLogout={onLogout} />
      
      <main className={`${mainClass} flex-1 p-4 md:p-8`}>
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="font-heading text-3xl font-bold text-white uppercase">Import List</h1>
            <p className="text-white/70 mt-1">Import leads from CSV - they'll be saved separately from your pipeline</p>
          </div>
          <Link to="/imported-lists">
            <Button variant="outline" className="border-white/[0.1] text-white/70 hover:bg-white/[0.06] rounded-none">
              <List className="w-5 h-5 mr-2" />
              View Imported Lists
            </Button>
          </Link>
        </div>

        {/* Info Banner */}
        <div className="bg-blue-500/10 border border-blue-500/30 p-4 mb-6 flex items-start gap-3">
          <AlertTriangle className="w-5 h-5 text-[#0A84FF] drop-shadow-[0_0_8px_rgba(10,132,255,0.4)] flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-[#0A84FF] drop-shadow-[0_0_8px_rgba(10,132,255,0.4)] font-medium">Imported lists are kept separate from your pipeline</p>
            <p className="text-white/70 text-sm mt-1">
              After importing, you can review leads and manually move them to your pipeline when ready.
              This keeps your pipeline clean and organized.
            </p>
          </div>
        </div>

        <div className="grid lg:grid-cols-2 gap-6">
          {/* Upload Section */}
          <div className="bg-[#0A0A0A]/80 backdrop-blur-sm border border-white/[0.08] p-6">
            <h2 className="text-white font-bold mb-4 flex items-center gap-2">
              <Upload className="w-5 h-5 text-gold" />Upload CSV File
            </h2>

            <div className="space-y-4 mb-4">
              <div>
                <Label className="text-white/70">List Name</Label>
                <Input
                  value={listName}
                  onChange={(e) => setListName(e.target.value)}
                  placeholder="e.g., Austin Direct Mail Campaign"
                  className="bg-black border-white/[0.08] text-white mt-1"
                />
              </div>
            </div>

            <div className="border-2 border-dashed border-white/[0.1] rounded-2xl p-8 text-center mb-4 hover:border-gold/50 transition-all duration-300">
              <Input
                type="file"
                accept=".csv"
                onChange={handleFileUpload}
                className="hidden"
                id="csv-upload"
              />
              <label htmlFor="csv-upload" className="cursor-pointer">
                <FileSpreadsheet className="w-12 h-12 text-white/70 mx-auto mb-3" />
                <p className="text-white/70 font-medium">Click to upload CSV</p>
                <p className="text-white/70 text-sm mt-1">or drag and drop</p>
              </label>
            </div>

            {file && (
              <div className="bg-black p-3 flex items-center gap-3 mb-4">
                <FileText className="w-5 h-5 text-gold" />
                <span className="text-white flex-1 truncate">{file.name}</span>
                <span className="text-white/70 text-sm">{parsedData.length} rows</span>
              </div>
            )}

            <Button variant="outline" onClick={downloadTemplate} className="w-full border-white/[0.1] text-white/70 hover:bg-white/[0.06] rounded-none">
              <Download className="w-4 h-4 mr-2" />Download Template
            </Button>
          </div>

          {/* Column Mapping */}
          <div className="bg-[#0A0A0A]/80 backdrop-blur-sm border border-white/[0.08] p-6">
            <h2 className="text-white font-bold mb-4">Column Mapping</h2>
            
            {parsedData.length > 0 ? (
              <div className="space-y-3 max-h-96 overflow-y-auto">
                {requiredFields.map(field => (
                  <div key={field.key} className="flex items-center gap-3">
                    <Label className="text-white/70 w-32 text-sm">
                      {field.label} {field.required && <span className="text-red-500">*</span>}
                    </Label>
                    <select
                      value={columnMapping[field.key] || ''}
                      onChange={(e) => setColumnMapping({ ...columnMapping, [field.key]: e.target.value })}
                      className="flex-1 bg-black border border-white/[0.08] text-white p-2 text-sm"
                    >
                      <option value="">-- Select column --</option>
                      {Object.keys(parsedData[0] || {}).map(col => (
                        <option key={col} value={col}>{col}</option>
                      ))}
                    </select>
                    {columnMapping[field.key] && (
                      <CheckCircle className="w-4 h-4 text-emerald-500" />
                    )}
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8 text-white/70">
                <FileSpreadsheet className="w-12 h-12 mx-auto mb-2 opacity-50" />
                <p>Upload a CSV to configure mapping</p>
              </div>
            )}
          </div>
        </div>

        {/* Preview & Import */}
        {parsedData.length > 0 && (
          <div className="bg-[#0A0A0A]/80 backdrop-blur-sm border border-white/[0.08] mt-6">
            <div className="flex items-center justify-between p-4 border-b border-white/[0.08]">
              <h2 className="text-white font-bold">Preview ({parsedData.length} records)</h2>
              <Button 
                onClick={handleImport} 
                disabled={importing || !columnMapping.property_address || !listName.trim()}
                className="gold-gradient-btn rounded-none font-bold"
              >
                {importing ? "Importing..." : `Import to "${listName || 'List'}"`}
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </div>

            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-black">
                  <tr>
                    <th className="text-left p-3 text-white/70">#</th>
                    {Object.keys(parsedData[0] || {}).slice(0, 6).map(col => (
                      <th key={col} className="text-left p-3 text-white/70 truncate max-w-32">{col}</th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-white/[0.06]">
                  {parsedData.slice(0, 5).map((row, idx) => (
                    <tr key={idx} className="hover:bg-white/[0.06]/50">
                      <td className="p-3 text-white/70">{idx + 1}</td>
                      {Object.values(row).slice(0, 6).map((val, i) => (
                        <td key={i} className="p-3 text-white truncate max-w-32">{val}</td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
              {parsedData.length > 5 && (
                <p className="text-center text-white/70 text-sm py-3">
                  ...and {parsedData.length - 5} more rows
                </p>
              )}
            </div>
          </div>
        )}

        {/* Import Result */}
        {importResult && (
          <div className="mt-6 p-6 border bg-emerald-900/20 border-emerald-500/50">
            <div className="flex items-center gap-3 mb-4">
              <CheckCircle className="w-6 h-6 text-emerald-500" />
              <h3 className="text-white font-bold text-lg">Import Successful!</h3>
            </div>
            
            <p className="text-white/70 mb-4">
              {importResult.total_leads} leads imported to "{importResult.name}"
            </p>
            
            <Link to="/imported-lists">
              <Button className="gold-gradient-btn rounded-none font-bold">
                View Imported Lists
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </Link>
          </div>
        )}
      </main>
    </div>
  );
}
